/**
 * Seek helpers for <video> elements (patterns from aka-mocap-viewer).
 *
 * createSeeker — coalescing scrub seeks: only one seek in flight; newer targets
 * collapse into a pending slot applied on `seeked`, so a fast scrub converges to
 * the release point instead of decoding every intermediate frame.
 *
 * seekExact — promisified single seek for export: waits for the frame to decode,
 * with a timeout guard so long-GOP seeks can never hang the export loop.
 */

export interface Seeker {
  seek(target: number): void;
  dispose(): void;
}

/** If `seeked` hasn't arrived after this long, assume it was lost and recover. */
const SEEK_WATCHDOG_MS = 3000;

export function createSeeker(video: HTMLVideoElement): Seeker {
  let seeking = false;
  let pending: number | null = null;
  let watchdog: ReturnType<typeof setTimeout> | undefined;

  function request(target: number): void {
    if (Math.abs(video.currentTime - target) < 0.01 && !video.seeking) return;
    if (seeking) {
      pending = target;
      return;
    }
    seeking = true;
    // Watchdog: browsers can drop a `seeked` event under decode pressure
    // (long-GOP sources especially). Without this, one lost event would
    // deadlock the seeker — every later scrub lands in `pending` and the
    // video never visibly updates again.
    clearTimeout(watchdog);
    watchdog = setTimeout(settle, SEEK_WATCHDOG_MS);
    video.currentTime = target;
  }

  function settle(): void {
    clearTimeout(watchdog);
    seeking = false;
    if (pending !== null) {
      const next = pending;
      pending = null;
      request(next);
    }
  }

  const onError = (): void => {
    clearTimeout(watchdog);
    seeking = false;
    pending = null;
  };

  video.addEventListener('seeked', settle);
  video.addEventListener('error', onError);

  return {
    seek: request,
    dispose: () => {
      clearTimeout(watchdog);
      video.removeEventListener('seeked', settle);
      video.removeEventListener('error', onError);
    },
  };
}

export function seekExact(video: HTMLVideoElement, t: number, timeoutMs = 3000): Promise<void> {
  return new Promise((resolve) => {
    if (Math.abs(video.currentTime - t) < 0.001) {
      resolve();
      return;
    }
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      video.removeEventListener('seeked', finish);
      resolve();
    };
    const timer = setTimeout(finish, timeoutMs);
    video.addEventListener('seeked', finish);
    video.currentTime = t;
  });
}
